// combatpro/panels/encounter/EncounterSummary.js
//
// Combat PRO - EncounterSummary (Stage 65: Encounter View Foundation).
//
// The encounter balance bar + live metric row at the bottom of view-encounter
// (battletracker.html #encounterSummary / #vi-balance-bar-wrap, 3510-3543). A
// faithful migration of the legacy imperative writes into a single declarative
// component: #vi-bal-players / #vi-bal-monsters (HP numbers), the two width-driven
// bars (#vi-bal-players-bar / #vi-bal-monsters-bar), and the four-metric row
// (#activeMonsters, #legendaryActionsRemaining, #threatLevel, #totalHP).
//
// Replaces the legacy DOM writes (viUpdateBalanceBar 8954-8969, the display half of
// updateEncounterSummary 9413-9415, and the #threatLevel write in
// updateThreatAssessment 9506-9507) with a render that re-derives from state every
// time it changes - the ~20 imperative updateEncounterSummary() call sites collapse
// into React re-render. All math is delegated to the pure encounterEngine; this
// component reads CombatContext state and renders, owning no state and mutating
// nothing.
//
// Markup, ids, inline styles, and text reproduce the master spec verbatim (no
// redesign). The only runtime-driven values are the bar widths, the two HP numbers,
// the four metric strings, and the #threatLevel colour (the legacy set
// .style.color). No DOM access, no getElementById. No optional chaining. No em dash.

import React, { useContext } from 'react';
import { CombatContext } from '../../contexts/CombatContext';
import {
  computeEncounterSummary,
  encounterSummaryDisplay,
  computeBalanceBar,
  computeThreat
} from './encounterEngine';

function EncounterSummary() {
  const { state } = useContext(CombatContext);
  const monsters = state.monsters;
  const players = state.players;
  const activeMonsterCount = state.activeMonsterCount || 0;

  const summary = computeEncounterSummary(monsters, activeMonsterCount);
  const display = encounterSummaryDisplay(summary);
  const balance = computeBalanceBar(players, monsters, activeMonsterCount);
  const threat = computeThreat(players, monsters, activeMonsterCount);

  return (
    <div id="encounterSummary" className="vi-encounter-summary" style={{ width: '100%' }}>
      <div id="vi-balance-bar-wrap" style={{ padding: '20px 22px', marginBottom: '12px', background: 'linear-gradient(180deg,#0F1A2A 0%,#0B1422 100%)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>
          <span style={{ color: '#4ADE80', fontSize: '18px', fontWeight: 600 }}>🧙 Players: <span id="vi-bal-players">{balance.playersHP}</span> HP</span>
          <span style={{ color: '#C9D1D9', fontWeight: 600, fontSize: '16px', letterSpacing: '0.5px' }}>Encounter Balance</span>
          <span style={{ color: '#F87171', fontSize: '18px', fontWeight: 600 }}>👹 Monsters: <span id="vi-bal-monsters">{balance.monstersHP}</span> HP</span>
        </div>
        <div id="vi-bar-track" style={{ position: 'relative', height: '32px', minHeight: '32px', borderRadius: '8px', overflow: 'hidden', background: 'rgba(255,255,255,0.15)', width: '100%' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, height: '32px', width: '100%', display: 'flex' }}>
            <div id="vi-bal-players-bar" style={{ height: '32px', width: balance.playersPct + '%', background: '#4ADE80', transition: 'width 0.4s' }}></div>
            <div id="vi-bal-monsters-bar" style={{ height: '32px', width: balance.monstersPct + '%', background: '#F87171', transition: 'width 0.4s' }}></div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '40px', marginTop: '14px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, textTransform: 'uppercase', color: '#9AA4B2', letterSpacing: '0.6px' }}>Active Monsters</div>
            <div id="activeMonsters" style={{ fontSize: '16px', fontWeight: 600, color: '#E6EDF3' }}>{display.activeMonsters}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, textTransform: 'uppercase', color: '#9AA4B2', letterSpacing: '0.6px' }}>Legendary Actions</div>
            <div id="legendaryActionsRemaining" style={{ fontSize: '16px', fontWeight: 600, color: '#E6EDF3' }}>{display.legendaryActionsRemaining}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, textTransform: 'uppercase', color: '#9AA4B2', letterSpacing: '0.6px' }}>Threat Level</div>
            <div id="threatLevel" style={{ fontSize: '16px', fontWeight: 600, color: threat.color }}>{threat.level}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, textTransform: 'uppercase', color: '#9AA4B2', letterSpacing: '0.6px' }}>Monster HP</div>
            <div id="totalHP" style={{ fontSize: '16px', fontWeight: 600, color: '#E6EDF3' }}>{display.totalHP}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EncounterSummary;
